import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { getErrorMessage } from '../utils';
import Axios from '../config';
import { useAuthContext } from '../contexts/AuthContext';
import TokenService from '../libs/token';
import Loader from '../components/Loader';
import logo from '../assets/logo.png';
import defaultImage from '../assets/anonymous.png';

// No need to import Collapse or Dropdown here when using the full Bootstrap bundle from CDN.
// import { Collapse } from 'bootstrap';
// import { Dropdown } from 'bootstrap';

/**
 * A responsive, industry-standard header for the NIN Enrolment application.
 *
 * This component provides navigation, user authentication status,
 * and application tracking functionality. It's designed to be clean,
 * modern, and easy to use across all devices.
 */
const FrontEndHeader = () => {
    // Media query hook to adapt layout for mobile/tablet devices (useful for other conditional rendering)
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 992px)' });

    const navigate = useNavigate();
    const [trackingId, setTrackingId] = useState('');
    const [trackingIdError, setTrackingIdError] = useState('');
    const [logoutLoading, setLogoutLoading] = useState(false);

    // Ref to the collapsible navbar element (main menu content)
    // We keep this ref primarily for programmatically closing the menu after actions,
    // not for initializing Bootstrap Collapse itself.
    const navbarCollapseRef = useRef(null);
    // State to track if the navbar is open/closed (for aria-expanded sync).
    // This can be simplified or removed if Bootstrap handles aria-expanded directly.
    const [isNavbarOpen, setIsNavbarOpen] = useState(false);
    // Ref to the header element itself to measure its height (not relevant to dropdown issue)
    const headerRef = useRef(null);

    // NEW: Ref to the user dropdown element - not needed for Bootstrap's automatic init
    // const userDropdownRef = useRef(null); // Remove this ref

    // Destructure state and dispatch from the authentication context
    const {
        authState: { isAuthenticated, user },
        authDispatch,
    } = useAuthContext();

    // Effect to fetch user data when authentication state changes
    useEffect(() => {
        const fetchUser = async () => {
            if (!user?.email) return;

            try {
                const response = await Axios.get('/users/profile', {
                    params: { email: user.email },
                });
                const userData = response.data;
                if (userData) {
                    TokenService.setUser(userData);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };

        if (isAuthenticated) {
            fetchUser();
        }
    }, [isAuthenticated, user?.email]);

    // Cleanup for the collapse menu when a link is clicked or action is performed.
    // We still need to programmatically hide the menu if an internal link is clicked.
    useEffect(() => {
        const currentNavbarCollapseEl = navbarCollapseRef.current;

        if (currentNavbarCollapseEl) {
            // Function to close the menu programmatically
            const closeMenu = () => {
                // Check if the navbar is currently shown by Bootstrap
                if (currentNavbarCollapseEl.classList.contains('show')) {
                    // Trigger Bootstrap's collapse hide method if the global BS object is available
                    if (window.bootstrap && window.bootstrap.Collapse) {
                        const bsCollapseInstance = window.bootstrap.Collapse.getInstance(currentNavbarCollapseEl);
                        if (bsCollapseInstance) {
                            bsCollapseInstance.hide();
                        }
                    }
                }
            };

            // Event listener for when Bootstrap Collapse shows/hides
            const handleBsShow = () => setIsNavbarOpen(true);
            const handleBsHide = () => setIsNavbarOpen(false);

            currentNavbarCollapseEl.addEventListener('show.bs.collapse', handleBsShow);
            currentNavbarCollapseEl.addEventListener('hide.bs.collapse', handleBsHide);

            // Get all clickable items within the menu that should close it
            // We specifically target direct navigation links or buttons that are not dropdown toggles
            const navLinks = currentNavbarCollapseEl.querySelectorAll('.nav-link:not(.dropdown-toggle), .dropdown-item');

            // Add event listeners to close menu on link click
            navLinks.forEach(item => {
                item.addEventListener('click', closeMenu);
            });

            // Cleanup function for useEffect
            return () => {
                currentNavbarCollapseEl.removeEventListener('show.bs.collapse', handleBsShow);
                currentNavbarCollapseEl.removeEventListener('hide.bs.collapse', handleBsHide);

                navLinks.forEach(item => {
                    item.removeEventListener('click', closeMenu);
                });
            };
        }
    }, []); // Empty dependency array means this runs once on mount/unmount

    // Handler for the navbar toggler button click
    // This will now just update our internal state, Bootstrap handles the actual toggling.
    const handleTogglerClick = () => {
        // Toggle the internal state to reflect the menu's open/closed status for aria-expanded
        // Bootstrap's JS handles the actual collapse/expand action
        setIsNavbarOpen(prevState => !prevState);
    };

    // Handles user logout process
    const handleLogout = async () => {
        setLogoutLoading(true);
        // Programmatically close the navbar collapse menu if it's open
        if (navbarCollapseRef.current && navbarCollapseRef.current.classList.contains('show')) {
            // Access Bootstrap's Collapse instance from the global window object
            if (window.bootstrap && window.bootstrap.Collapse) {
                const bsCollapseInstance = window.bootstrap.Collapse.getInstance(navbarCollapseRef.current);
                if (bsCollapseInstance) {
                    bsCollapseInstance.hide();
                }
            }
        }
        try {
            const storedUser = user;
            if (storedUser?.refreshToken) {
                await Axios.patch('/users/logout', { refreshToken: storedUser.refreshToken });
            }
            TokenService.removeUser();
            authDispatch({ type: 'LOG_OUT' });
            setLogoutLoading(false);

            swal('Success', 'You have been logged out.', 'success').then(() => {
                navigate('/');
            });
        } catch (error) {
            setLogoutLoading(false);
            swal('Logout Failed', getErrorMessage(error), 'error');
        }
    };

    // Utility function to check if the user has one of the specified roles
    const isAuthorizedRole = (roles = []) => roles.includes(user?.role);

    const maskEmail = email => {
        if (!email.includes('@')) return email; // Handle invalid emails

        const [localPart, domain] = email.split('@');
        const maskedLocal = localPart.length > 2
            ? localPart.charAt(0) + '*'.repeat(Math.min(localPart.length - 2, 3)) + localPart.charAt(localPart.length - 1)
            : localPart;

        return `${maskedLocal}@${domain}`;
    };

    // Handles the submission of the application tracking form
    const handleTrackingSearchSubmit = (e) => {
        e.preventDefault();
        if (!trackingId.trim()) {
            setTrackingIdError('Please enter a valid Application Tracking ID.');
            return;
        }
        setTrackingIdError('');
        
        // Close menu after search (optional, but good for mobile UX)
        if (navbarCollapseRef.current && navbarCollapseRef.current.classList.contains('show')) {
            if (window.bootstrap && window.bootstrap.Collapse) {
                const bsCollapseInstance = window.bootstrap.Collapse.getInstance(navbarCollapseRef.current);
                if (bsCollapseInstance) {
                    bsCollapseInstance.hide();
                }
            }
        }
        
        // Navigate to track enrollment page with the tracking ID
        navigate(`/track-enrollment?id=${encodeURIComponent(trackingId.trim())}`);
    };

    return (
        <>
            {/* Full-screen modal for loading state during logout */}
            {logoutLoading && (
                <div className="modal-overlay d-flex justify-content-center align-items-center">
                    <Loader />
                </div>
            )}

            <header ref={headerRef} className="shadow-sm sticky-top" style={{ zIndex: 1030 }}>
                <nav
                    className="navbar navbar-expand-lg navbar-dark"
                    style={{ backgroundColor: '#00401A' }}
                    aria-label="Main navigation"
                >
                    <div className="container-fluid">
                        {/* Logo */}
                        <Link to="/" className="navbar-brand">
                            <img src={logo} alt="NIN Enrolment Portal Logo"
                                onError={(e) => e.target.src = 'https://placehold.co/170x30/00401A/FFFFFF?text=NIN+Enroll'}
                                width='170'
                                height='30'
                            />
                        </Link>

                        {/* Navbar Toggler for mobile */}
                        <button
                            className="navbar-toggler"
                            type="button"
                            data-bs-toggle="collapse" // IMPORTANT: Add this
                            data-bs-target="#mainNavbar" // IMPORTANT: Add this
                            aria-controls="mainNavbar"
                            aria-expanded={isNavbarOpen} // Dynamically set based on React state (can be handled by BS)
                            aria-label="Toggle navigation"
                            onClick={handleTogglerClick} // Keep your onClick if you need to update internal state
                        >
                            <span className="navbar-toggler-icon"></span>
                        </button>

                        {/* Collapsible Navbar content */}
                        {/* Keep ref here if you need to programmatically close it, but Bootstrap handles collapse behaviour */}
                        <div className="collapse navbar-collapse" id="mainNavbar" ref={navbarCollapseRef}>
                            {/* Main Navigation Links */}
                            <ul className="navbar-nav me-auto mb-2 mt-2 mb-lg-0">
                                <li className="nav-item">
                                    <NavLink to="/" end className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                                        Home
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/about" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                                        About
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/services" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                                        Services
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/centers" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                                        Find a Centre
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/track-enrollment" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                                        Track Enrollment
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/faq" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                                        FAQ
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/contact" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                                        Contact
                                    </NavLink>
                                </li>
                            </ul>

                            {/* Enrollment Tracking Search Form */}
                            <form
                                className="d-flex me-lg-3 mt-2 mt-lg-0"
                                role="search"
                                onSubmit={handleTrackingSearchSubmit}
                            >
                                <div className="input-group">
                                    <input
                                        className={`form-control form-control-sm ${trackingIdError ? 'is-invalid' : ''}`}
                                        type="search"
                                        placeholder="Track Enrollment..."
                                        aria-label="Track Enrollment"
                                        value={trackingId}
                                        onChange={(e) => {
                                            setTrackingId(e.target.value);
                                            if (trackingIdError) setTrackingIdError('');
                                        }}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleTrackingSearchSubmit(e);
                                            }
                                        }}
                                    />
                                    <button className="btn btn-warning btn-sm" type="submit" disabled={!trackingId.trim()}>
                                        <i className="bi bi-search"></i>
                                    </button>
                                </div>
                                {trackingIdError && (
                                    <div className="invalid-feedback d-block mt-1">
                                        {trackingIdError}
                                    </div>
                                )}
                            </form>

                            {/* User Authentication Section */}
                            <div className="navbar-nav ms-lg-auto mt-2 mt-lg-0">
                                {isAuthenticated && user ? (
                                    // Authenticated User Dropdown
                                    <div className="nav-item dropdown">
                                        <a
                                            className="nav-link dropdown-toggle d-flex align-items-center"
                                            href="#"
                                            id="userDropdown"
                                            role="button"
                                            data-bs-toggle="dropdown" // This is correct for Bootstrap to handle
                                            aria-expanded="false"
                                        // Removed ref={userDropdownRef} as it's not needed for automatic init
                                        >
                                            <img
                                                src={user.image || defaultImage}
                                                alt="User Avatar"
                                                className="rounded-circle me-2"
                                                width="32"
                                                height="32"
                                                onError={(e) => e.target.src = 'https://placehold.co/32x32/cccccc/FFFFFF?text=User'}
                                            />
                                            Hi, {user.firstName?.split(' ')[0] || 'User'}
                                        </a>
                                        <ul
                                            className="dropdown-menu dropdown-menu-end"
                                            aria-labelledby="userDropdown"
                                        >
                                            <li>
                                                <div className="px-3 py-2 text-muted">
                                                    <div className="fw-bold" style={{ wordBreak: 'break-word' }}>{user.firstName}</div>
                                                    <small style={{ wordBreak: 'break-word' }}>{maskEmail(user.email)}</small>
                                                </div>
                                            </li>
                                            <li><hr className="dropdown-divider" /></li>
                                            <li>
                                                <Link to="/account" className="dropdown-item">
                                                    <i className="bi bi-person-circle me-2"></i>Account
                                                </Link>
                                            </li>
                                            {isAuthorizedRole([
                                                'SuperAdmin',
                                                'Admin',
                                                'Moderator',
                                                'Agent',
                                                'Partner',
                                                'Auditor',
                                                'Support',
                                                'Manager'
                                            ]) && (
                                                    <li>
                                                        <Link to='/dashboard' className='dropdown-item'>
                                                            <i className='bi bi-speedometer me-2'></i>
                                                            Dashboard
                                                        </Link>
                                                    </li>
                                                )}
                                            <li>
                                                <Link to="/enrollments" className="dropdown-item">
                                                    <i className="bi bi-journal-text me-2"></i>My Enrollments
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/support/my-tickets" className="dropdown-item">
                                                    <i className="bi bi-life-preserver me-2"></i>Support Tickets
                                                </Link>
                                            </li>
                                            <li><hr className="dropdown-divider" /></li>
                                            <li>
                                                <button onClick={handleLogout} className="dropdown-item text-danger">
                                                    <i className="bi bi-box-arrow-right me-2"></i>Logout
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                ) : (
                                    // Login/Register Buttons for Guests
                                    <div className="d-flex align-items-center mt-2 mt-lg-0">
                                        <Link to="/login" className="btn btn-outline-light btn-sm me-2">
                                            Login
                                        </Link>
                                        <Link to="/register" className="btn btn-warning btn-sm">
                                            Register
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </nav>
            </header>
        </>
    );
};

export default FrontEndHeader;